/**
 * Native Haptics Module
 * Uses @capacitor/haptics when running natively, falls back to web Vibration API
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

export type HapticPattern = 
  | 'tap'           // Single short tap
  | 'doubleTap'     // Two quick taps
  | 'success'       // Celebratory pattern
  | 'error'         // Alert pattern
  | 'warning'       // Attention pattern
  | 'connecting'    // Pulsing while connecting
  | 'connected'     // Call connected
  | 'disconnected'  // Call ended
  | 'ringing'       // Incoming/outgoing ring pulse
  | 'dtmf';         // Key press

// Check if we're running natively
const isNative = Capacitor.isNativePlatform();

// Web fallback patterns (for browser testing)
const webPatterns: Record<HapticPattern, number | number[]> = {
  tap: 10,
  doubleTap: [10, 50, 10],
  dtmf: 15,
  success: [50, 50, 50, 50, 100],
  error: [100, 50, 100, 50, 100],
  warning: [50, 100, 50],
  connecting: [30, 200, 30, 200, 30],
  connected: [100, 50, 200],
  disconnected: [50, 30, 50, 30, 100],
  ringing: [100, 500],
};

/**
 * Check if haptics are supported
 */
export const isHapticSupported = (): boolean => {
  if (isNative) {
    return true; // Capacitor haptics always available on native
  }
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

/**
 * Play a haptic pattern using native haptics when available
 */
export const haptic = async (pattern: HapticPattern): Promise<boolean> => {
  if (!isHapticSupported()) {
    return false;
  }

  try {
    if (isNative) {
      // Use native Capacitor haptics
      switch (pattern) {
        case 'tap':
        case 'dtmf':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case 'doubleTap':
          await Haptics.impact({ style: ImpactStyle.Light });
          setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 50);
          break;
        case 'success':
        case 'connected':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
        case 'warning':
        case 'disconnected':
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case 'connecting':
          // Pulsing pattern
          await Haptics.impact({ style: ImpactStyle.Medium });
          setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 200);
          setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 400);
          break;
        case 'ringing':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        default:
          await Haptics.impact({ style: ImpactStyle.Medium });
      }
      return true;
    } else {
      // Web fallback using Vibration API
      const vibrationPattern = webPatterns[pattern];
      return navigator.vibrate(vibrationPattern);
    }
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
    return false;
  }
};

/**
 * Stop any ongoing vibration (web only)
 */
export const stopHaptic = (): boolean => {
  if (isNative) {
    // Native haptics are instant, nothing to stop
    return true;
  }
  
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    return navigator.vibrate(0);
  }
  return false;
};

/**
 * Selection changed haptic (for pickers, toggles)
 */
export const hapticSelection = async (): Promise<void> => {
  if (isNative) {
    await Haptics.selectionChanged();
  } else {
    haptic('tap');
  }
};

/**
 * Repeat a pattern at an interval (e.g., for ringing)
 */
export class HapticLoop {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private pattern: HapticPattern;
  private intervalMs: number;

  constructor(pattern: HapticPattern, intervalMs: number = 2000) {
    this.pattern = pattern;
    this.intervalMs = intervalMs;
  }

  start(): void {
    if (!isHapticSupported()) return;
    
    // Play immediately
    haptic(this.pattern);
    
    // Then repeat at interval
    this.intervalId = setInterval(() => {
      haptic(this.pattern);
    }, this.intervalMs);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    stopHaptic();
  }
}

// Convenience functions for common patterns
export const hapticTap = () => haptic('tap');
export const hapticSuccess = () => haptic('success');
export const hapticError = () => haptic('error');
export const hapticWarning = () => haptic('warning');
export const hapticConnecting = () => haptic('connecting');
export const hapticConnected = () => haptic('connected');
export const hapticDisconnected = () => haptic('disconnected');
export const hapticDtmf = () => haptic('dtmf');
